<template>
  <GoogleMap ref="googleMapRef"
             v-bind="$attrs"
             @click="handleMeasureMapClick"
             :api-promise="apiPromise"
             :styles="resolvedMapStyles"
             :map-type-control-options="mapTypeControlOptions"
             :mapId="resolvedMapId">
    <CustomControl v-if="loaded && enableMeasureTool" position="TOP_LEFT" :index="2" class="measure-wrapper">
      <button
          class="measure-toggle"
          :class="{ active: measureMode }"
          type="button"
          @click="toggleMeasureMode"
      >
        {{ measureMode ? $t('measure.exit') : $t('measure.open') }}
      </button>
    </CustomControl>
    <CustomControl
        v-if="loaded && measureMode && measurePath.length > 0"
        position="BOTTOM_CENTER"
        :index="100"
    >
      <div class="measure-panel">
        <div class="measure-panel__title">{{ $t('measure.title') }}</div>
        <div class="measure-panel__hint">
          {{ measureClosed ? $t('measure.hint.closed') : $t('measure.hint.open') }}
        </div>
        <div v-if="measureClosed" class="measure-panel__line">
          {{ $t('measure.total-area') }}: {{ formattedMeasureArea }}
        </div>
        <div class="measure-panel__line">
          {{ $t('measure.total-distance') }}: {{ formattedMeasureDistance }}
        </div>
        <div class="measure-panel__actions">
          <button type="button" @click="clearMeasure">{{ $t('generic.clear') }}</button>
          <button type="button" @click="toggleMeasureMode">{{ $t('generic.close') }}</button>
        </div>
      </div>
    </CustomControl>
    <Polyline
        v-if="loaded && measureMode && !measureClosed && measurePath.length > 0"
        :options="measurePolylineOptions"
    />
    <Polygon
        v-if="loaded && measureMode && measureClosed && measurePath.length > 2"
        :options="measurePolygonOptions"
    />
    <Marker
        v-for="(position, index) in measurePath"
        :key="`measure-vertex-${index}-${position.lat}-${position.lng}`"
        :options="getMeasureVertexMarkerOptions(position)"
        @drag="event => onMeasureVertexDrag(index, event)"
        @click="() => onMeasureVertexClick(index)"
    />
    <Marker
        v-for="(segment, index) in measureSegmentMarkers"
        :key="`measure-segment-${index}-${segment.position.lat}-${segment.position.lng}`"
        :options="segment.options"
    />
    <div v-if="loaded">
      <slot></slot>
    </div>
  </GoogleMap>
</template>

<script>
import {CustomControl, GoogleMap, Marker, Polygon, Polyline} from "vue3-google-map";
import {dataManipulator} from "@/assets/js/data-manipulation";
import {gmLoader} from "@/assets/js/google-maps";

export default {
  name: "Map",
  components: {CustomControl, GoogleMap, Marker, Polygon, Polyline},
  inheritAttrs: false,
  props: {
    useMapId: {
      type: Boolean,
      default: false
    },
    mapId: {
      type: String,
      default: () => process.env.VUE_APP_GOOGLE_MAP_ID
    },
    useDefaultMapStyles: {
      type: Boolean,
      default: true
    },
    mapStyles: {
      type: Array,
      default: null
    },
    enableMeasureTool: {
      type: Boolean,
      default: false
    }
  },
  emits: ['onMapReady', 'measure-updated'],
  data() {
    return {
      apiPromise: gmLoader.loadPromise(),
      defaultMapStyles: [
        {
          featureType: "poi",
          elementType: "labels",
          stylers: [
            { visibility: "off" }
          ]
        },
        {
          featureType: "transit",
          elementType: "labels",
          stylers: [
            { visibility: "off" }
          ]
        }
      ],
      loaded: false,
      measureMode: false,
      measureClosed: false,
      measurePath: [],
      measureDistanceMeters: 0,
      measureAreaSquareMeters: 0,
      measureSegmentMarkers: [],
      measureListeners: [],
      initialDisableDoubleClickZoom: null
    };
  },
  computed: {
    mapTypeControlOptions() {
      return {
        mapTypeIds: ['roadmap', 'hybrid']
      };
    },
    resolvedMapId() {
      return this.useMapId ? this.mapId : undefined;
    },
    resolvedMapStyles() {
      if (!this.resolvedMapId) {
        return undefined;
      }

      if (this.mapStyles !== null) {
        return this.mapStyles;
      }

      return this.useDefaultMapStyles ? this.defaultMapStyles : undefined;
    },
    map() {
      return this.$refs.googleMapRef.map;
    },
    formattedMeasureDistance() {
      return this.formatDistance(this.measureDistanceMeters);
    },
    formattedMeasureArea() {
      return this.formatArea(this.measureAreaSquareMeters);
    },
    measurePolylineOptions() {
      return {
        path: this.measurePath.map(point => ({
          lat: point.lat,
          lng: point.lng
        })),
        clickable: false,
        geodesic: true,
        strokeColor: '#202124',
        strokeOpacity: 1,
        strokeWeight: 2.5
      };
    },
    measurePolygonOptions() {
      return {
        paths: this.measurePath.map(point => ({
          lat: point.lat,
          lng: point.lng
        })),
        clickable: false,
        geodesic: true,
        strokeColor: '#202124',
        strokeOpacity: 1,
        strokeWeight: 2.5,
        fillColor: '#202124',
        fillOpacity: 0.08
      };
    }
  },
  mounted() {
    dataManipulator.ensureCondition(() => window.google.maps, () => this.mapReady());
  },
  beforeUnmount() {
    this.disposeMeasureTool();
  },
  methods: {
    mapReady() {
      if (this.enableMeasureTool) {
        this.initMeasureTool();
      }

      this.loaded = true;
      this.$emit('onMapReady');
    },
    initMeasureTool() {
      this.initialDisableDoubleClickZoom = this.map.get('disableDoubleClickZoom');
      ['maptypeid_changed', 'renderingtype_changed', 'projection_changed', 'idle', 'tilesloaded'].forEach(eventName => {
        this.measureListeners.push(
            this.map.addListener(eventName, () => this.onMapStateChanged())
        );
      });
    },
    disposeMeasureTool() {
      this.clearMeasure();
      this.measureListeners.forEach(listener => listener.remove());
      this.measureListeners = [];
    },
    toggleMeasureMode() {
      this.measureMode = !this.measureMode;

      if (this.measureMode) {
        this.map.setOptions({
          draggableCursor: 'crosshair',
          disableDoubleClickZoom: true
        });
        this.clearMeasure();
        return;
      }

      this.map.setOptions({
        draggableCursor: null,
        disableDoubleClickZoom: this.initialDisableDoubleClickZoom
      });
      this.clearMeasure();
    },
    onMapStateChanged() {
      if (!this.measureMode) {
        this.clearMeasure();
      }
    },
    handleMeasureMapClick(event) {
      if (!this.measureMode || this.measureClosed) {
        return;
      }

      const latLng = event?.latLng;
      if (!latLng) {
        return;
      }

      this.measurePath.push({
        lat: latLng.lat(),
        lng: latLng.lng()
      });
      this.refreshMeasureVisualsAndStats();
    },
    closeMeasureShape() {
      if (this.measurePath.length < 3 || this.measureClosed) {
        return;
      }

      this.measureClosed = true;
      this.refreshMeasureVisualsAndStats();
    },
    clearMeasure() {
      this.measureClosed = false;
      this.measurePath = [];
      this.measureDistanceMeters = 0;
      this.measureAreaSquareMeters = 0;
      this.measureSegmentMarkers = [];
      this.emitMeasureUpdate();
    },
    refreshMeasureVisualsAndStats() {
      this.renderSegmentMarkers();
      this.updateMeasureStats();
    },
    renderSegmentMarkers() {
      const google = window.google;
      const segments = [];
      const markers = [];

      for (let index = 1; index < this.measurePath.length; index++) {
        segments.push([this.measurePath[index - 1], this.measurePath[index]]);
      }

      if (this.measureClosed && this.measurePath.length > 2) {
        segments.push([this.measurePath[this.measurePath.length - 1], this.measurePath[0]]);
      }

      segments.forEach(([from, to]) => {
        const fromLatLng = new google.maps.LatLng(from.lat, from.lng);
        const toLatLng = new google.maps.LatLng(to.lat, to.lng);
        markers.push({
          position: google.maps.geometry.spherical.interpolate(fromLatLng, toLatLng, 0.5),
          options: {
            position: google.maps.geometry.spherical.interpolate(fromLatLng, toLatLng, 0.5),
            clickable: false,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 0
            },
            label: {
              text: this.formatDistance(google.maps.geometry.spherical.computeDistanceBetween(fromLatLng, toLatLng)),
              color: '#202124',
              fontSize: '12px',
              fontWeight: '600'
            },
            zIndex: 900
          }
        });
      });

      this.measureSegmentMarkers = markers;
    },
    updateMeasureStats() {
      const google = window.google;
      const path = this.measurePath.map(point => ({lat: point.lat, lng: point.lng}));

      if (this.measureClosed && path.length > 2) {
        path.push(path[0]);
      }

      this.measureDistanceMeters = path.length > 1
          ? google.maps.geometry.spherical.computeLength(path)
          : 0;
      this.measureAreaSquareMeters = this.measureClosed && this.measurePath.length > 2
          ? google.maps.geometry.spherical.computeArea(this.measurePath)
          : 0;
      this.emitMeasureUpdate();
    },
    emitMeasureUpdate() {
      this.$emit('measure-updated', {
        active: this.measureMode,
        closed: this.measureClosed,
        path: this.measurePath,
        distanceMeters: this.measureDistanceMeters,
        areaSquareMeters: this.measureAreaSquareMeters
      });
    },
    getMeasureVertexMarkerOptions(position) {
      return {
        position,
        draggable: true,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: '#ffffff',
          fillOpacity: 1,
          scale: 6,
          strokeColor: '#202124',
          strokeWeight: 2
        },
        zIndex: 1000
      };
    },
    onMeasureVertexDrag(index, event) {
      const latLng = event?.latLng;
      if (!latLng) {
        return;
      }

      this.measurePath.splice(index, 1, {
        lat: latLng.lat(),
        lng: latLng.lng()
      });
      this.refreshMeasureVisualsAndStats();
    },
    onMeasureVertexClick(index) {
      if (index === 0 && this.measurePath.length >= 3 && !this.measureClosed) {
        this.closeMeasureShape();
      }
    },
    formatDistance(distanceMeters) {
      if (distanceMeters < 1000) {
        return `${distanceMeters.toFixed(0)} ${this.$t('generic.meters')}`;
      }

      return `${(distanceMeters / 1000).toFixed(2)} ${this.$t('generic.kilometers')}`;
    },
    formatArea(areaSquareMeters) {
      if (areaSquareMeters < 1000000) {
        return `${areaSquareMeters.toFixed(0)} ${this.$t('measure.square-meters')}`;
      }

      return `${(areaSquareMeters / 1000000).toFixed(2)} ${this.$t('measure.square-kilometers')}`;
    }
  }
}
</script>

<style scoped lang="scss">

.measure-wrapper {
  padding-top: 10px ;
}

.measure-toggle,
.measure-panel__actions button {
  border: 0;
  border-radius: 999px;
  background: #fff;
  color: #202124;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  box-shadow: 0 2px 10px rgba(32, 33, 36, 0.2);
}

.measure-toggle {
  padding: 12px 18px;
}

.measure-toggle.active {
  background: #202124;
  color: #fff;
}

.measure-panel {
  min-width: 260px;
  max-width: 320px;
  padding: 16px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.96);
  color: #202124;
  box-shadow: 0 12px 35px rgba(32, 33, 36, 0.24);
}

.measure-panel__title {
  font-size: 16px;
  font-weight: 700;
}

.measure-panel__hint {
  margin-top: 6px;
  color: #5f6368;
  font-size: 13px;
}

.measure-panel__line {
  margin-top: 10px;
  font-size: 14px;
  font-weight: 600;
}

.measure-panel__actions {
  display: flex;
  gap: 8px;
  margin-top: 14px;
}

.measure-panel__actions button {
  padding: 10px 14px;
}
</style>
